/**
 * Dashboard - Boekhouding overzicht
 * Gebaseerd op Morris dashboard.html
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import { boekhoudingService, DashboardData } from '../../services/boekhouding.service';

interface DisplayData {
  jaar: number;
  kwartalen: { kwartaal: number; inkomsten: number; uitgaven: number; resultaat: number }[];
  totaalInkomsten: number;
  totaalUitgaven: number;
  totaalResultaat: number;
  btw: {
    beginSaldo: number;
    btwAfTeDragen: number;
    voorbelasting: number;
    priveGebruik: number;
    betaald: number;
  };
  bankStand: number;
  kasStand: number;
}

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DisplayData | null>(null);

  // Huidig boekjaar uit localStorage
  const huidigJaar = parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const apiData: DashboardData = await boekhoudingService.getDashboard(huidigJaar);

        // Transformeer API data naar display format
        // API retourneert kwartalen met inkomsten/uitgaven/resultaat (Morris stijl)
        // Gebruik Number() omdat Pydantic Decimals als strings serialiseert
        const kwartalen = (apiData.kwartalen || []).map(q => ({
          kwartaal: q.kwartaal,
          inkomsten: Number(q.inkomsten) || 0,
          uitgaven: Number(q.uitgaven) || 0,
          resultaat: Number(q.resultaat) || 0,
        }));

        // Als geen kwartalen, maak lege kwartalen
        if (kwartalen.length === 0) {
          for (let i = 1; i <= 4; i++) {
            kwartalen.push({ kwartaal: i, inkomsten: 0, uitgaven: 0, resultaat: 0 });
          }
        }

        const displayData: DisplayData = {
          jaar: apiData.jaar,
          kwartalen,
          // Gebruik totalen direct van API (Morris stijl)
          totaalInkomsten: Number(apiData.totaal_inkomsten) || 0,
          totaalUitgaven: Number(apiData.totaal_uitgaven) || 0,
          totaalResultaat: Number(apiData.resultaat) || 0,
          btw: {
            beginSaldo: Number(apiData.btw_begin_saldo) || 0,
            btwAfTeDragen: Number(apiData.btw_verkoop) || 0,
            voorbelasting: Number(apiData.btw_voorbelasting) || 0,
            priveGebruik: Number(apiData.btw_prive_auto) || 0,
            betaald: Number(apiData.btw_betaald) || 0,
          },
          bankStand: Number(apiData.bank_saldo) || 0,
          kasStand: Number(apiData.kas_saldo) || 0,
        };
        setData(displayData);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [huidigJaar]);

  const formatBedrag = (bedrag: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(bedrag);
  };

  if (loading) {
    return (
      <BoekhoudingLayout title={`Dashboard ${huidigJaar}`}>
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  if (!data) {
    return (
      <BoekhoudingLayout title={`Dashboard ${huidigJaar}`}>
        <div className={styles.card}>
          <p>Geen data beschikbaar</p>
        </div>
      </BoekhoudingLayout>
    );
  }

  // Gebruik totalen direct van API data
  const totaalInkomsten = data.totaalInkomsten;
  const totaalUitgaven = data.totaalUitgaven;
  const totaalResultaat = data.totaalResultaat;
  const btwEindstand = data.btw.beginSaldo + data.btw.btwAfTeDragen - data.btw.voorbelasting + data.btw.priveGebruik + (data.btw.betaald * -1);

  return (
    <BoekhoudingLayout title={`Dashboard ${data.jaar}`}>
      {/* Mobiele Quick Actions */}
      <div className={`${styles.mobileQuickActions} ${styles.mobileOnly}`}>
        <Link to="/boekhouding/scan?type=inkoop" className={`${styles.quickActionCard} ${styles.quickActionPrimary}`}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="12" cy="12" r="3"/>
            <path d="M3 9h2M19 9h2M3 15h2M19 15h2M9 3v2M15 3v2M9 19v2M15 19v2"/>
          </svg>
          <span>Scan Inkoop</span>
        </Link>
        <div className={styles.quickStatCard}>
          <span className={`${styles.statIcon} ${styles.statIconBank}`}>B</span>
          <div className={styles.statContent}>
            <span className={styles.statLabel}>Bank</span>
            <span className={styles.statValue}>{formatBedrag(data.bankStand)}</span>
          </div>
        </div>
        <div className={styles.quickStatCard}>
          <span className={`${styles.statIcon} ${styles.statIconKas}`}>K</span>
          <div className={styles.statContent}>
            <span className={styles.statLabel}>Kas</span>
            <span className={styles.statValue}>{formatBedrag(data.kasStand)}</span>
          </div>
        </div>
      </div>

      <div className={styles.dashboardGrid}>
        {/* Resultaten overzicht */}
        <div className={`${styles.card} ${styles.cardLarge}`}>
          <h3>Resultaat {data.jaar}</h3>
          <div className={styles.statsRow}>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Inkomsten</span>
              <span className={`${styles.statValue} ${styles.positive}`}>{formatBedrag(totaalInkomsten)}</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Uitgaven</span>
              <span className={`${styles.statValue} ${styles.negative}`}>{formatBedrag(totaalUitgaven)}</span>
            </div>
            <div className={`${styles.stat} ${styles.statHighlight}`}>
              <span className={styles.statLabel}>Resultaat</span>
              <span className={`${styles.statValue} ${totaalResultaat >= 0 ? styles.positive : styles.negative}`}>
                {formatBedrag(totaalResultaat)}
              </span>
            </div>
          </div>
        </div>

        {/* BTW overzicht */}
        <div className={`${styles.card} ${styles.cardLarge}`}>
          <h3>BTW {data.jaar}</h3>
          <table className={styles.btwTable}>
            <thead>
              <tr>
                <th></th>
                <th className={styles.alignRight}>Debet</th>
                <th className={styles.alignRight}>Credit</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Begin schuld 1-1-{data.jaar}</td>
                <td className={styles.alignRight}>{formatBedrag(data.btw.beginSaldo)}</td>
                <td></td>
              </tr>
              <tr>
                <td>BTW verkoop</td>
                <td className={styles.alignRight}>{formatBedrag(data.btw.btwAfTeDragen)}</td>
                <td></td>
              </tr>
              <tr>
                <td>BTW inkoop (voorbelasting)</td>
                <td></td>
                <td className={styles.alignRight}>{formatBedrag(data.btw.voorbelasting)}</td>
              </tr>
              {data.btw.priveGebruik !== 0 && (
              <tr>
                <td>Privé gebruik auto</td>
                <td className={styles.alignRight}>{formatBedrag(data.btw.priveGebruik)}</td>
                <td></td>
              </tr>
              )}
              <tr>
                <td>Ontvangen teruggaves</td>
                <td className={styles.alignRight}>{formatBedrag(data.btw.betaald * -1)}</td>
                <td></td>
              </tr>
            </tbody>
            <tfoot>
              <tr className={styles.btwTotal}>
                <td><strong>Eindstand 31-12-{data.jaar}</strong></td>
                <td className={styles.alignRight}><strong>{formatBedrag(btwEindstand)}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          <Link to="/boekhouding/btw" className={styles.btnLink}>Bekijk details</Link>
        </div>

        {/* Kwartaal overzicht */}
        <div className={`${styles.card} ${styles.cardFull}`}>
          <h3>Per Kwartaal</h3>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Kwartaal</th>
                <th className={styles.alignRight}>Inkomsten</th>
                <th className={styles.alignRight}>Uitgaven</th>
                <th className={styles.alignRight}>Resultaat</th>
              </tr>
            </thead>
            <tbody>
              {data.kwartalen.map((q) => (
                <tr key={q.kwartaal}>
                  <td>Q{q.kwartaal}</td>
                  <td className={styles.alignRight}>{formatBedrag(q.inkomsten)}</td>
                  <td className={styles.alignRight}>{formatBedrag(q.uitgaven)}</td>
                  <td className={`${styles.alignRight} ${q.resultaat >= 0 ? styles.positive : styles.negative}`}>
                    {formatBedrag(q.resultaat)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Snelle acties */}
        <div className={styles.card}>
          <h3>Snelle Acties</h3>
          <div className={styles.quickActions}>
            <Link to="/boekhouding/boeking/nieuw?boekstuk=bank" className={`${styles.btn} ${styles.btnPrimary}`}>+ Bank boeking</Link>
            <Link to="/boekhouding/boeking/nieuw?boekstuk=kas" className={`${styles.btn} ${styles.btnSecondary}`}>+ Kas boeking</Link>
            <Link to="/boekhouding/boeking/nieuw?boekstuk=verkoop" className={`${styles.btn} ${styles.btnSuccess}`}>+ Verkoop</Link>
            <Link to="/boekhouding/boeking/nieuw?boekstuk=inkoop" className={`${styles.btn} ${styles.btnWarning}`}>+ Inkoop</Link>
          </div>
        </div>
      </div>
    </BoekhoudingLayout>
  );
};

export default Dashboard;
